import * as THREE from 'three';
import { BIMElement, DuctElement, PipeElement } from './types';

export interface Connector {
    id: string; // Unique ID for the connector
    elementId: string;
    position: THREE.Vector3;
    direction: THREE.Vector3; // Outward normal
    systemType: string;
    shape: 'Round' | 'Rectangular';
    size: number | [number, number]; // Diameter or [Width, Height]
}

export class ConnectorManager {
    /**
     * Get all connectors for a given element.
     */
    static getConnectors(element: BIMElement): Connector[] {
        const connectors: Connector[] = [];
        const geom = (element as any).geometryData;
        if (!geom) return connectors;

        if (element.category === 'Ducts') {
            const duct = element as DuctElement;
            const path = duct.geometryData.path;
            if (!path || path.length < 2) return connectors;
            const start = new THREE.Vector3(...path[0]);
            const end = new THREE.Vector3(...path[path.length - 1]);
            const dir = end.clone().sub(start).normalize();

            // Start Connector
            connectors.push({
                id: `${element.id}-start`,
                elementId: element.id,
                position: start,
                direction: dir.clone().negate(),
                systemType: element.parameters['System Type'] || 'Undefined',
                shape: 'Rectangular',
                size: [duct.geometryData.width, duct.geometryData.height]
            });

            // End Connector
            connectors.push({
                id: `${element.id}-end`,
                elementId: element.id,
                position: end,
                direction: dir,
                systemType: element.parameters['System Type'] || 'Undefined',
                shape: 'Rectangular',
                size: [duct.geometryData.width, duct.geometryData.height]
            });
        } else if (element.category === 'Pipes') {
            const pipe = element as PipeElement;
            const path = pipe.geometryData.path;
            if (!path || path.length < 2) return connectors;
            const start = new THREE.Vector3(...path[0]);
            const end = new THREE.Vector3(...path[path.length - 1]);
            const dir = end.clone().sub(start).normalize();

            // Start Connector
            connectors.push({
                id: `${element.id}-start`,
                elementId: element.id,
                position: start,
                direction: dir.clone().negate(),
                systemType: element.parameters['System Type'] || 'Undefined',
                shape: 'Round',
                size: pipe.geometryData.diameter
            });

            // End Connector
            connectors.push({
                id: `${element.id}-end`,
                elementId: element.id,
                position: end,
                direction: dir,
                systemType: element.parameters['System Type'] || 'Undefined',
                shape: 'Round',
                size: pipe.geometryData.diameter
            });
        }

        // TODO: Add support for Fittings (Elbows, Tees)

        return connectors;
    }

    /**
     * Find the closest connector to a given point within a tolerance.
     */
    static findClosestConnector(
        point: THREE.Vector3,
        elements: BIMElement[],
        tolerance: number = 0.2, // 20cm
        excludeElementId?: string
    ): Connector | null {
        let closest: Connector | null = null;
        let minDist = Infinity;

        for (const el of elements) {
            if (el.id === excludeElementId) continue;
            if (el.category !== 'Ducts' && el.category !== 'Pipes' && el.category !== 'Duct Fittings' && el.category !== 'Pipe Fittings') continue;

            const connectors = this.getConnectors(el);
            for (const conn of connectors) {
                const dist = point.distanceTo(conn.position);
                if (dist < tolerance && dist < minDist) {
                    minDist = dist;
                    closest = conn;
                }
            }
        }

        return closest;
    }
}
